import React, {FormEvent, useEffect, useState} from 'react';

import {useStore} from 'effector-react';
import {Location} from 'history';
import {useTranslation} from 'react-i18next';
import {Redirect, useLocation} from 'react-router-dom';

import {LanguageSelector} from 'components/common/LanguageSelector';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Checkbox} from 'components/ui-kit/Checkbox';
import {Hint} from 'components/ui-kit/Hint';
import {Input} from 'components/ui-kit/Input';
import {Link} from 'components/ui-kit/Link';
import {TitleLogoComponent} from 'components/ui-kit/TitleLogo';
import {IS_TJ} from 'core/constants/env';
import {LABELS} from 'core/constants/labels';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {
    doLogin,
    isAuth$,
    isCustomsRole$,
    isNkRole$,
    loginError$,
    loginFx,
    tempAuthData$,
} from 'models/auth';

import {CertAuthModal} from './CertAuthModal';
import {CertRegisterModal} from './CertRegisterModal';
import {
    AuthContent,
    AuthDescription,
    AuthTitle,
    ButtonContainer,
    CheckboxLabel,
    Field,
    Fields,
    LanguageWrapper,
    MainContainer,
    ServiceTitle,
} from './styled';

export function LoginPage() {
    const isCustomsRole = useStore(isCustomsRole$);
    const isNkRole = useStore(isNkRole$);
    const {t} = useTranslation();
    const isAuth = useStore(isAuth$);
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [saveCredentials, setSaveCredentials] = useState(true);
    const [showCertAuthModal, setShowCertAuthModal] = useState(false);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const pending = useStore(loginFx.pending);
    const error = useStore(loginError$);
    const location = useLocation<{from: Location}>();

    const tempAuthData = useStore(tempAuthData$);

    useEffect(() => {
        if (tempAuthData) history.push(ROUTE.TWO_FA);
    }, [tempAuthData]);

    const {search} = useLocation();

    useEffect(() => {
        const query = new URLSearchParams(search);
        const token = query.get('token');
        if (token) {
            const {login, password} = JSON.parse(atob(token));
            if (login && password) {
                setLogin(login);
                setPassword(password);
                doLogin({login, password, saveCredentials});
            }
        }
    }, []);

    function onSubmit(e?: FormEvent) {
        e?.preventDefault();
        doLogin({login, password, saveCredentials});
    }

    if (isAuth) {
        if (isNkRole) {
            return <Redirect to={ROUTE.COMPLAINTS} />;
        }

        if (isCustomsRole) {
            return <Redirect to={ROUTE.DECLARATIONS} />;
        }

        return <Redirect to={ROUTE.DASHBOARD} />;
    }

    const fastAuthValue = new URLSearchParams(location.search).get('auth');
    if (fastAuthValue) {
        const [loginValue, passwordValue] = decodeURIComponent(escape(atob(fastAuthValue))).split(
            ',',
        );
        doLogin({login: loginValue, password: passwordValue, saveCredentials});
    }

    return (
        <>
            <div>
                <ServiceTitle size={4}>
                    <TitleLogoComponent />
                    {LABELS.name}
                </ServiceTitle>
                <LanguageWrapper>
                    <LanguageSelector />
                </LanguageWrapper>
            </div>
            <MainContainer>
                <AuthContent>
                    <AuthTitle size={3}>{t('pages.login.title')}</AuthTitle>
                    <form onSubmit={onSubmit}>
                        <AuthDescription>{t('pages.login.description')}</AuthDescription>
                        <Hint isError={error !== null}>
                            <Fields>
                                <Field>
                                    <Input
                                        id="login"
                                        data-id="input-login"
                                        placeholder={t('pages.login.loginPlaceholder')}
                                        autoComplete="off"
                                        disabled={pending}
                                        value={login}
                                        onChange={(e) => setLogin(e.target.value)}
                                        hasError={error !== null}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        id="password"
                                        data-id="input-password"
                                        placeholder={t('pages.login.passwordPlaceholder')}
                                        value={password}
                                        type="password"
                                        autoComplete="off"
                                        disabled={pending}
                                        onChange={(e) => setPassword(e.target.value)}
                                        hasError={error !== null}
                                    />
                                </Field>
                                <Field>
                                    <Checkbox
                                        id="remember-me"
                                        data-id="cb-remember"
                                        onChange={() => setSaveCredentials(!saveCredentials)}
                                        checked={saveCredentials}
                                    >
                                        <CheckboxLabel>{t('pages.login.rememberMe')}</CheckboxLabel>
                                    </Checkbox>
                                </Field>
                                <Field>
                                    <Link to={ROUTE.REQUEST_RESET}>{t('pages.login.forgot')}</Link>
                                </Field>
                            </Fields>
                        </Hint>
                        <ButtonContainer>
                            <Button type={'submit'} isLoading={pending} data-id="btn-submit">
                                {t('pages.login.submitButton')}
                            </Button>
                            {IS_TJ && (
                                <>
                                    <Button
                                        type="button"
                                        onClick={() => setShowCertAuthModal(true)}
                                    >
                                        {t('pages.login.certs.login')}
                                    </Button>
                                    <Button
                                        variant={ButtonVariant.Text}
                                        type="button"
                                        onClick={() => setShowRegisterModal(true)}
                                    >
                                        {t('pages.login.register')}
                                    </Button>
                                </>
                            )}
                        </ButtonContainer>
                    </form>
                </AuthContent>
            </MainContainer>
            {showCertAuthModal && (
                <CertAuthModal show={showCertAuthModal} setShow={setShowCertAuthModal} />
            )}
            {showRegisterModal && (
                <CertRegisterModal show={showRegisterModal} setShow={setShowRegisterModal} />
            )}
        </>
    );
}
